import * as THREE from 'three';
import { renderCtx } from './RenderContext';

/** Colour grade applied after tone mapping (see `GradeEffect`). */
export interface GradeLook {
  saturation: number;
  contrast: number;
  exposure: number;
  /** Added to the darkest tones (tinted blacks). */
  shadowTint: [number, number, number];
  /** Added to the brightest tones (warm or icy highlights). */
  highlightTint: [number, number, number];
}

export interface BiomeLook {
  sky: number;
  groundBounce: number;
  hemiIntensity: number;
  sunColor: number;
  sunIntensity: number;
  sunDir: [number, number, number];
  rimColor: number;
  rimIntensity: number;
  fogColor: number;
  fogNear: number;
  fogFar: number;
  background: number;
  shadowIntensity: number;
  /** Image-based light strength (`scene.environmentIntensity`). */
  envIntensity: number;
  /** Fresnel rim added to characters (separates them from the floor). */
  charRim: number;
  charRimIntensity: number;
  /** Multiplier for prop light/fire sockets (dungeons lean on them). */
  fireScale: number;
  grade: GradeLook;
  /** Bloom threshold override (lower in dark places so fire and runes glow). */
  bloomThreshold?: number;
}

const grade = (saturation: number, contrast: number, exposure: number, shadowTint: [number, number, number], highlightTint: [number, number, number]): GradeLook => ({
  saturation,
  contrast,
  exposure,
  shadowTint,
  highlightTint,
});

const WARM_HI: [number, number, number] = [0.02, 0.012, 0.0];
const ICY_HI: [number, number, number] = [0.0, 0.01, 0.022];
const TEAL_LO: [number, number, number] = [0.0, 0.012, 0.018];

/**
 * Daylight biomes (fields, towns, sea): warm key, cool sky fill and a cool
 * rim, per the "neutral world, saturated light" direction.
 */
export const BIOMES: Record<string, BiomeLook> = {
  meadow: {
    sky: 0xb9ccff, groundBounce: 0x4f4230, hemiIntensity: 1.0, sunColor: 0xffe0b0, sunIntensity: 3.3,
    sunDir: [-0.45, 0.8, 0.35], rimColor: 0x9fd0ff, rimIntensity: 0.9, fogColor: 0x92a6ae, fogNear: 48,
    fogFar: 160, background: 0x7d8e96, shadowIntensity: 0.82, envIntensity: 0.45, charRim: 0xbfdcff, charRimIntensity: 0.14,
    fireScale: 1, grade: grade(0.96, 1.06, 1.1, TEAL_LO, WARM_HI),
  },
  town: {
    sky: 0xc4d2ff, groundBounce: 0x544232, hemiIntensity: 1.0, sunColor: 0xffd9a8, sunIntensity: 3.3,
    sunDir: [-0.5, 0.78, 0.3], rimColor: 0xa8d4ff, rimIntensity: 0.8, fogColor: 0x9aa4a8, fogNear: 52,
    fogFar: 170, background: 0x8c979b, shadowIntensity: 0.82, envIntensity: 0.45, charRim: 0xc4dcff, charRimIntensity: 0.12,
    fireScale: 1, grade: grade(0.95, 1.06, 1.08, TEAL_LO, WARM_HI),
  },
  desert: {
    sky: 0xd8ccb0, groundBounce: 0x6b4c2e, hemiIntensity: 0.9, sunColor: 0xffdcaa, sunIntensity: 3.6,
    sunDir: [-0.35, 0.85, 0.4], rimColor: 0xffd2a0, rimIntensity: 0.6, fogColor: 0xc4aa88, fogNear: 50,
    fogFar: 160, background: 0xb89c78, shadowIntensity: 0.88, envIntensity: 0.4, charRim: 0xffe6c8, charRimIntensity: 0.12,
    fireScale: 1, grade: grade(0.92, 1.07, 1.05, [0.012, 0.006, 0.0], WARM_HI),
  },
  frost: {
    sky: 0xb4c8ee, groundBounce: 0x5a6a80, hemiIntensity: 1.15, sunColor: 0xfff0e2, sunIntensity: 2.7,
    sunDir: [-0.4, 0.72, 0.45], rimColor: 0x9fd8ff, rimIntensity: 1.0, fogColor: 0x9cb0cc, fogNear: 36,
    fogFar: 135, background: 0x7d92b0, shadowIntensity: 0.72, envIntensity: 0.5, charRim: 0xcfe6ff, charRimIntensity: 0.16,
    fireScale: 1.2, grade: grade(0.9, 1.05, 1.08, [0.0, 0.008, 0.024], ICY_HI),
  },
  rift: {
    sky: 0x6a5a8c, groundBounce: 0x1e0c22, hemiIntensity: 0.9, sunColor: 0xd8c4f4, sunIntensity: 1.2,
    sunDir: [-0.3, 0.9, 0.3], rimColor: 0xff5ab8, rimIntensity: 1.1, fogColor: 0x1c0c26, fogNear: 26,
    fogFar: 92, background: 0x0c0412, shadowIntensity: 0.7, envIntensity: 0.25, charRim: 0xffa8e0, charRimIntensity: 0.22,
    fireScale: 1.5, grade: grade(0.82, 1.1, 1.14, [0.01, 0.0, 0.02], [0.02, 0.0, 0.016]), bloomThreshold: 0.72,
  },
  /** Generic dark interior: dim cool key and cool fill so warm fire pools carry the frame. */
  abyss: {
    sky: 0x6a7c96, groundBounce: 0x2a2420, hemiIntensity: 1.1, sunColor: 0xbcc8e0, sunIntensity: 1.15,
    sunDir: [-0.35, 0.9, 0.25], rimColor: 0x7fa8d8, rimIntensity: 0.6, fogColor: 0x11171b, fogNear: 28,
    fogFar: 96, background: 0x0b0f12, shadowIntensity: 0.62, envIntensity: 0.25, charRim: 0xa4c8ff, charRimIntensity: 0.22,
    fireScale: 1.6, grade: grade(0.94, 1.08, 1.14, TEAL_LO, WARM_HI), bloomThreshold: 0.74,
  },
  sea: {
    sky: 0xbcd6f4, groundBounce: 0x2e5060, hemiIntensity: 1.05, sunColor: 0xfff2d8, sunIntensity: 3.2,
    sunDir: [-0.4, 0.75, 0.45], rimColor: 0xbfe6ff, rimIntensity: 0.8, fogColor: 0xa6c4dc, fogNear: 70,
    fogFar: 260, background: 0x9cbcd6, shadowIntensity: 0.7, envIntensity: 0.55, charRim: 0xd0ecff, charRimIntensity: 0.14,
    fireScale: 1, grade: grade(0.98, 1.05, 1.08, TEAL_LO, WARM_HI),
  },
  arena: {
    sky: 0xb4bcd4, groundBounce: 0x3e3028, hemiIntensity: 0.95, sunColor: 0xffdcb8, sunIntensity: 2.9,
    sunDir: [-0.45, 0.8, 0.35], rimColor: 0xa0c8ff, rimIntensity: 0.9, fogColor: 0x505866, fogNear: 42,
    fogFar: 135, background: 0x3a404c, shadowIntensity: 0.82, envIntensity: 0.4, charRim: 0xc0d8ff, charRimIntensity: 0.16,
    fireScale: 1.2, grade: grade(0.94, 1.08, 1.06, TEAL_LO, WARM_HI),
  },
};

/**
 * Hand-tuned looks for specific instances (zone id, or id prefix before the
 * gate/floor number). Dungeons read like the reference: dark but readable,
 * neutral stone, teal-black shadows, warm brazier pools and a cool rim.
 */
const ZONE_LOOKS: Record<string, BiomeLook> = {
  crypt: {
    ...BIOMES.abyss!,
    sky: 0x6c7e8c, groundBounce: 0x2a2620, fogColor: 0x10161a, background: 0x0a0e10,
    grade: grade(0.95, 1.08, 1.16, [0.0, 0.014, 0.014], WARM_HI),
  },
  tomb: {
    ...BIOMES.abyss!,
    sky: 0x8a8272, groundBounce: 0x3a2e22, hemiIntensity: 1.05, sunColor: 0xe0d8c8, sunIntensity: 1.3,
    fogColor: 0x19140f, background: 0x100c08, charRim: 0xb0d0ff,
    grade: grade(0.9, 1.08, 1.14, [0.0, 0.01, 0.016], [0.022, 0.014, 0.004]),
  },
  keep: {
    ...BIOMES.abyss!,
    sky: 0x7a92b8, groundBounce: 0x28303e, hemiIntensity: 1.15, sunColor: 0xc4d6f4, sunIntensity: 1.45,
    rimColor: 0x8ec8ff, rimIntensity: 0.9, fogColor: 0x182232, fogNear: 30, fogFar: 105, background: 0x0f1522,
    charRim: 0xb8dcff, fireScale: 1.5, grade: grade(0.9, 1.07, 1.12, [0.0, 0.008, 0.026], ICY_HI),
  },
  deepvault: {
    ...BIOMES.abyss!,
    sky: 0x4e8290, groundBounce: 0x10221f, hemiIntensity: 1.1, sunColor: 0xa4d4e0, sunIntensity: 1.2,
    rimColor: 0x5ae0d0, rimIntensity: 0.9, fogColor: 0x0a1a1e, fogNear: 28, fogFar: 100, background: 0x061014,
    charRim: 0x98f0e8, grade: grade(0.92, 1.08, 1.14, [0.0, 0.016, 0.018], [0.004, 0.016, 0.016]),
  },
  siege: {
    ...BIOMES.abyss!,
    sky: 0x60687e, groundBounce: 0x241618, hemiIntensity: 1.0, sunColor: 0xc9c6d4, sunIntensity: 1.25,
    sunDir: [-0.4, 0.85, 0.3], rimColor: 0xff5a3a, rimIntensity: 1.1, fogColor: 0x170d0d, fogNear: 30,
    fogFar: 110, background: 0x0b0506, charRim: 0xffb088, charRimIntensity: 0.2, fireScale: 1.0,
    grade: grade(0.92, 1.1, 1.12, [0.016, 0.002, 0.0], [0.024, 0.01, 0.0]),
  },
  rift: BIOMES.rift!,
  colossus_cinderhide: {
    ...BIOMES.abyss!,
    sky: 0x6a6272, groundBounce: 0x5c2412, hemiIntensity: 1.0, sunColor: 0xe2d2c6, sunIntensity: 1.6,
    sunDir: [-0.4, 0.82, 0.35], rimColor: 0xff7a3a, rimIntensity: 1.2, fogColor: 0x241210, fogNear: 38,
    fogFar: 125, background: 0x120806, charRim: 0xffb080, charRimIntensity: 0.18, fireScale: 1.3,
    grade: grade(0.9, 1.08, 1.1, [0.012, 0.004, 0.004], [0.024, 0.01, 0.0]), bloomThreshold: 0.8,
  },
  colossus_gloomfang: {
    ...BIOMES.meadow!,
    sky: 0x5a6a9a, groundBounce: 0x1a2418, hemiIntensity: 1.05, sunColor: 0xb8c8ff, sunIntensity: 1.6,
    sunDir: [-0.35, 0.82, 0.4], rimColor: 0x9ab0ff, rimIntensity: 1.1, fogColor: 0x1a2232, fogNear: 38,
    fogFar: 125, background: 0x0e1322, envIntensity: 0.3, charRim: 0xb8ccff, charRimIntensity: 0.22, fireScale: 1.5,
    grade: grade(0.88, 1.08, 1.14, [0.004, 0.006, 0.026], ICY_HI), bloomThreshold: 0.76,
  },
  colossus_rimebound: { ...BIOMES.frost!, sunIntensity: 2.9, fogNear: 45, fogFar: 150 },
  colossus_dunestrider: {
    ...BIOMES.desert!,
    sky: 0x9aa4b8, groundBounce: 0x5a4832, sunColor: 0xeee0cc, sunIntensity: 2.7, rimColor: 0xa8c0ff, rimIntensity: 1.0,
    fogColor: 0x8e8474, fogNear: 45, fogFar: 140, background: 0x6e6a62, charRim: 0xd0dcff,
    grade: grade(0.86, 1.1, 1.04, [0.0, 0.006, 0.016], WARM_HI),
  },
  crucible: { ...BIOMES.arena!, sunColor: 0xffd4a8, sunIntensity: 3.0, sunDir: [-0.55, 0.7, 0.3] },
  glimmer: { ...BIOMES.meadow!, sky: 0xaed2ff, sunColor: 0xfff0d0, sunIntensity: 3.4, fogColor: 0x9cc0d4, background: 0x8cb4cc },
};

/** The look for a zone: an instance-specific look, else its biome, else meadow. */
export function lookForZone(layout: { id: string; biome: string }): BiomeLook {
  const id = layout.id;
  const prefix = id.replace(/_\d+$/, '');
  return ZONE_LOOKS[id] ?? ZONE_LOOKS[prefix] ?? BIOMES[layout.biome] ?? (BIOMES.meadow as BiomeLook);
}

/**
 * "Warm key, cool shadows, rim" lighting rig. The sun's orthographic shadow
 * box follows the player and snaps to shadow-map texels so shadows don't
 * shimmer while moving.
 */
export class Lighting {
  readonly hemi: THREE.HemisphereLight;
  readonly sun: THREE.DirectionalLight;
  readonly rim: THREE.DirectionalLight;
  look: BiomeLook = BIOMES.meadow as BiomeLook;
  private sunDir = new THREE.Vector3(-0.45, 0.8, 0.35).normalize();
  private R = new THREE.Matrix4();
  private Rinv = new THREE.Matrix4();
  private tmp = new THREE.Vector3();
  private rimOffset = new THREE.Vector3(14, 12, -32);
  private halfSize = 26;
  private qualityVersion = -1;

  constructor(readonly scene: THREE.Scene) {
    this.hemi = new THREE.HemisphereLight(0xb9ccff, 0x3a2e24, 0.9);
    this.sun = new THREE.DirectionalLight(0xffe2b8, 3.0);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.03;
    this.sun.shadow.radius = 2;
    const sc = this.sun.shadow.camera;
    sc.near = 1;
    sc.far = 180;
    this.setShadowSize(26);
    this.rim = new THREE.DirectionalLight(0x9fd0ff, 0.8);
    scene.add(this.hemi, this.sun, this.sun.target, this.rim, this.rim.target);
    this.updateBasis();
  }

  setShadowSize(half: number): void {
    this.halfSize = half;
    const sc = this.sun.shadow.camera;
    sc.left = -half;
    sc.right = half;
    sc.top = half;
    sc.bottom = -half;
    sc.updateProjectionMatrix();
  }

  setShadowMapSize(size: number): void {
    if (this.sun.shadow.mapSize.x === size) return;
    this.sun.shadow.mapSize.set(size, size);
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
  }

  private updateBasis(): void {
    this.R.lookAt(this.sunDir, new THREE.Vector3(), new THREE.Vector3(0, 1, 0));
    this.Rinv.copy(this.R).transpose();
  }

  applyBiome(look: BiomeLook): void {
    this.look = look;
    this.hemi.color.setHex(look.sky);
    this.hemi.groundColor.setHex(look.groundBounce);
    this.hemi.intensity = look.hemiIntensity;
    this.sun.color.setHex(look.sunColor);
    this.sun.intensity = look.sunIntensity;
    this.sun.shadow.intensity = look.shadowIntensity;
    this.rim.color.setHex(look.rimColor);
    this.rim.intensity = look.rimIntensity;
    this.sunDir.set(...look.sunDir).normalize();
    this.updateBasis();
    this.scene.fog = new THREE.Fog(look.fogColor, look.fogNear, look.fogFar);
    this.scene.background = new THREE.Color(look.background);
    // Read by GameRenderer.setScene (grade, bloom, image-based light).
    this.scene.userData.look = look;
  }

  /** Re-applies quality-dependent shadow settings when the preset changed. */
  private syncQuality(): void {
    if (this.qualityVersion === renderCtx.version) return;
    this.qualityVersion = renderCtx.version;
    const q = renderCtx.profile;
    this.sun.castShadow = q.shadows;
    this.setShadowMapSize(q.shadowMapSize);
    this.sun.shadow.radius = q.shadowRadius;
    this.setShadowSize(q.shadowHalf);
  }

  /** Keeps the shadow frustum centred on the focus point, snapped to texels. */
  follow(focus: THREE.Vector3): void {
    this.syncQuality();
    const texel = (this.halfSize * 2) / this.sun.shadow.mapSize.x;
    this.tmp.copy(focus).applyMatrix4(this.Rinv);
    this.tmp.x = Math.round(this.tmp.x / texel) * texel;
    this.tmp.y = Math.round(this.tmp.y / texel) * texel;
    this.tmp.applyMatrix4(this.R);
    this.sun.target.position.copy(this.tmp);
    this.sun.position.copy(this.tmp).addScaledVector(this.sunDir, 90);
    this.rim.target.position.copy(this.tmp);
    this.rim.position.copy(this.tmp).add(this.rimOffset);
  }
}
